let messageStore = null;
let messageStoreReady = false;

/**
 * Clears the message store by setting it to null and marking it as not ready.
 * Also removes the "messageStore" item from local storage.
 * @public
 * @returns {void}
 */
const clearMessageStore = () => {
	messageStore = null;
	messageStoreReady = false;
	localStorage.removeItem("messageStore");
};

/**
 * Initializes the message store by merging the provided messages with the existing messages
 * stored in localStorage. Ensures that there are no duplicate messages.
 * @example
 * ```
 * initMessageStore({
 *  'key1': 'Message 1',
 *  'key2': 'Message 2'
 * });
 * ```
 * @param {object} messages - An object containing the messages to be stored.
 * @public
 * @throws {Error} Throws an error if unable to save the message store to local storage.
 * @returns {void}
 */
const initMessageStore = (messages) => {
	try {
		messageStore = JSON.parse(localStorage.getItem("messageStore"));

		if (messageStore) {
			// We need to merge the messages with the existing messages but,
			// we also need to ensure that we avoid duplicates.
			Object.keys(messages).forEach((key) => {
				if (!messageStore[key]) {
					messageStore[key] = messages[key];
				}
			});
		} else {
			messageStore = messages;
		}

		localStorage.setItem("messageStore", JSON.stringify(messageStore));
		messageStoreReady = true;
	} catch (error) {
		throw new Error(
			"Unable to save message store to local storage.",
			error.message,
		);
	}
};

/**
 * Retrieves a localized message based on the provided key.
 * @param {string} key - The key for the desired localized message.
 * @returns {string} The localized message corresponding to the key, or a default message indicating the key is missing.
 */
const getLocalizedMessage = (key) => {
	if (!messageStore) {
		try {
			messageStore = JSON.parse(localStorage.getItem("messageStore"));
		} catch (error) {
			throw new Error(
				"Unable to retrieve message store from local storage.",
				error.message,
			);
		}
	}

	return messageStore[key] || `Missing translation for key: ${key}`;
};

const isMessageStoreReady = () => messageStoreReady;

export {
	clearMessageStore,
	getLocalizedMessage,
	isMessageStoreReady,
	initMessageStore,
};
